import { h } from '@omni-torn/ui';
import {
  cashOf,
  costEach,
  fifoMatch,
  fromTradeLine,
  keyMeets,
  profitByItem,
  profitCsvRows,
  profitTotals,
  receiptLines,
  receiptsOf,
  saleEach,
  SOURCE_NAME,
  withoutReceipt,
  type MatchedSale,
  type ProfitLine,
  type ProfitSource,
  type TradeReceipt,
  filterTrades,
  findLogCategory,
  inRange,
  ledgerByItem,
  listingsWorth,
  MARKET_KEEP_MS,
  rangeText,
  readItemMarket,
  readMyBazaar,
  readMyItemMarket,
  readTradeLine,
  sumPerDay,
  tradesCsvRows,
  tradeTotals,
  VENUE_NAME,
  vsCheapest,
  type DateRange,
  type ItemMarketAnswer,
  type MyListing,
  type Side,
  type TradeLine,
  type Venue,
} from '@omni-torn/shared';
import { forgetLogText, logKeptWhere, readingLogText } from '../own-history';
import type { RefItem } from '@omni-torn/shared/torn-ref';
import { firstRead, LogReader, MultiReader, type LogReaderLike, type LogStream } from '../log-kit/reader';
import { logStore, type LogStore } from '../log-kit/store';
import { csvButton, filterSelect, keptLogPanel, rangeFromAddress, rangePicker, setAddressQuery, zoomChart } from '../log-kit/ui';
import { server, ServerError, session, torn, tornCallsLeft } from '../session';
import { box, gated, loading, lockedCard, money, num, problem, short, signed, table, tabs, tct } from '../ui';
import { ref } from './torn';

/**
 * Trading (You → Trading, website roadmap S26 — torn.report's Item Market and Bazaar pages): your Item Market and bazaar
 * ledgers from your log, what you have listed now against the cheapest listing, totals and profit.
 *  - Your log (Full key): the "Item market" and "Bazaar" log categories (found by name in Torn's `torn/logcategories`,
 *    else the whole log), read into this browser by the log-report kit (S09) — buys, sales, fees, per item, per day.
 *  - Your Item Market listings: v2 `/user/itemmarket` (Limited key), one call (a page of 100; more pages only if you have
 *    more). Your bazaar: v1 `/user/?selections=bazaar` (any key), one call.
 *  - The cheapest listing of an item: the Market page's server read (S11, `/api/market/item/:id`, kept 3 minutes; your
 *    key isn't used), on your click — one row or "Load all". When the server's share for you is used up, your own key
 *    reads Torn's public Item Market (within this site's share of your calls).
 * Profit (roadmap S27 — weav3r's premium "did the trade make money", like the "Arbitrage" script): every sale matched
 * first in, first out to your earliest buys of that item still held (packages/shared trade-profit.ts) — over the whole
 * log kept in this browser plus your trades' receipts (the extension's trade helper keeps a receipt when you copy it;
 * they come with your lists, `/api/me/lists`, and "Remove" takes one out there). Profit per sale, per item and per day;
 * what can't be costed (an unpriced line, nothing bought before) is counted apart, never guessed.
 * Your own key and log only, in your browser; nothing new goes to or is kept on the Omni-Torn server (receipts are your
 * lists, already kept there by lists sync).
 */

type Tab = 'market' | 'bazaar' | 'totals' | 'profit';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'market', label: 'Item Market' },
  { id: 'bazaar', label: 'Bazaar' },
  { id: 'totals', label: 'Totals' },
  { id: 'profit', label: 'Profit' },
];
const LOG_NEED = { level: 4, user: ['log'] } as const;
const LISTINGS_NEED = { level: 3, user: ['itemmarket'] } as const;
const DAY = 86_400_000;
/** This site's calls to leave for other pages when your own key reads a price. */
const SPARE = 3;
/** Pages of your Item Market listings read at most (100 each). */
const LISTING_PAGES = 3;

const MARKET_URL = (id: number) => `https://www.torn.com/page.php?sid=ItemMarket#/market/view=search&itemID=${id}`;

/** Cheapest Item Market price per item, this visit (as long as the server keeps its copy). */
const cheapestKept = new Map<number, { at: number; price: number | null }>();

const tile = (label: string, value: string, data = '', cls = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: `tile-value ${cls}`.trim() }, value));
const tone = (n: number | null) => (n === null || n === 0 ? '' : n > 0 ? 't-green' : 't-red');
const moneySigned = (n: number | null) => signed(n === null ? null : Math.round(n), (x) => money(x));

/** The cheapest Item Market listing of an item now (null: nobody lists it). */
async function cheapestOf(id: number): Promise<number | null> {
  const kept = cheapestKept.get(id);
  if (kept && Date.now() - kept.at < MARKET_KEEP_MS) return kept.price;
  let price: number | null;
  try {
    const a = await server<ItemMarketAnswer>('GET', `/api/market/item/${id}`);
    price = a.listings.length ? Math.min(...a.listings.map((l) => l.price)) : null;
  } catch (err) {
    // The server's share for you (or in all) is used this minute: Torn's public answer with your own key, if this site's
    // share of your calls has room.
    if (!(err instanceof ServerError && (err.status === 429 || err.status === 503)) || tornCallsLeft() <= SPARE) throw err;
    const r = readItemMarket(await torn(`/v2/market/${id}/itemmarket`, { limit: '20' }, MARKET_KEEP_MS), id);
    price = r?.listings.length ? r.listings[0]!.price : null;
  }
  cheapestKept.set(id, { at: Date.now(), price });
  return price;
}

export async function renderTrading(view: HTMLElement): Promise<void> {
  const me = session.get();
  if (!me) return;
  const pid = me.playerId;
  const q = new URLSearchParams(location.search);
  let tab: Tab = TABS.find((t) => t.id === q.get('tab'))?.id ?? 'market';
  let range: DateRange = rangeFromAddress('30d');
  let side: Side | null = q.get('side') === 'buy' || q.get('side') === 'sell' ? (q.get('side') as Side) : null;
  let itemFilter = Number(q.get('item')) || null;
  const SOURCES: readonly ProfitSource[] = ['market', 'bazaar', 'trade'];
  let source: ProfitSource | null = SOURCES.find((x) => x === q.get('where')) ?? null;
  let items = new Map<number, RefItem>();
  const name = (id: number) => items.get(id)?.name ?? `Item ${id}`;
  const itemLink = (id: number, label?: string | null) => h('a', { href: `/market/${id}`, 'data-link': true }, label || name(id));

  const status = h('p', { class: 'muted small', 'data-trading-status': '' });
  const body = h('div', {});
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Trading'), h('p', { class: 'muted' }, `Your Item Market, bazaar and trades: what you bought and sold, the fees, profit per sale, item and day (first in, first out — receipts from the trade helper count your trades), and what you have listed now against the cheapest — from your own key and log, ${logKeptWhere()}.`)),
    tabs(TABS, tab, (id) => {
      tab = id as Tab;
      setQ();
      draw();
    }),
    status,
    body,
  );
  const setQ = () => setAddressQuery(range, { tab: tab === 'market' ? null : tab, side: tab === 'profit' ? null : side, where: tab === 'profit' ? source : null, item: itemFilter ? String(itemFilter) : null });

  // ------------------------------------------------------------ what you have listed now

  /** One "Cheapest now" cell per listing, filled on a click (one row) or "Load all". */
  function listingTable(list: readonly MyListing[], venue: Venue): HTMLElement {
    const cells = new Map<string, HTMLElement>();
    const vsCells = new Map<string, HTMLElement>();
    const fill = (l: MyListing, price: number | null | 'error', err?: unknown) => {
      const c = cells.get(l.key);
      const v = vsCells.get(l.key);
      if (!c || !v) return;
      if (price === 'error') {
        c.replaceChildren(h('span', { class: 't-red', title: err instanceof Error ? err.message : '' }, 'Couldn’t read'));
        return;
      }
      c.replaceChildren(price === null ? h('span', { class: 'muted' }, 'nobody') : h('a', { href: MARKET_URL(l.itemId), target: '_blank', rel: 'noopener' }, money(price)));
      const cmp = vsCheapest(l.price, price);
      v.dataset.vs = cmp.state;
      v.replaceChildren(
        cmp.state === 'unknown'
          ? h('span', { class: 'muted' }, '—')
          : cmp.state === 'cheapest'
            ? h('span', { class: 't-green', 'data-vs': 'cheapest' }, cmp.diff! < 0 ? `Cheapest (${money(-cmp.diff!)} under)` : 'Cheapest')
            : h('span', { class: 't-red', 'data-vs': 'beaten' }, `${money(cmp.diff)} over (+${num(cmp.pct, 1)}%)`),
      );
    };
    /** One item's price, into every row of it. */
    const load = async (itemId: number): Promise<boolean> => {
      const rows = list.filter((l) => l.itemId === itemId);
      for (const l of rows) cells.get(l.key)?.replaceChildren(h('span', { class: 'muted' }, '…'));
      try {
        const price = await cheapestOf(itemId);
        for (const l of rows) fill(l, price);
        return true;
      } catch (err) {
        for (const l of rows) fill(l, 'error', err);
        note.textContent = err instanceof Error ? err.message : 'Couldn’t read the price.';
        return false;
      }
    };
    const note = h('span', { class: 'muted small', 'data-prices-note': '' });
    const all = h('button', { type: 'button', class: 'btn-small', 'data-action': `load-all-${venue}` }, 'Load all prices') as HTMLButtonElement;
    all.addEventListener('click', async () => {
      all.disabled = true;
      note.textContent = ' Reading prices…';
      const ids = [...new Set(list.map((l) => l.itemId))];
      let done = 0;
      for (const id of ids) {
        if (!(await load(id))) break;
        done++;
      }
      if (done === ids.length) note.textContent = ` ${num(done)} item${done === 1 ? '' : 's'} priced.`;
      else note.textContent += ` (${num(done)} of ${num(ids.length)} priced — “Load all prices” again in a minute goes on.)`;
      all.disabled = false;
    });
    const t = table(list, [
      { label: 'Item', get: (l) => itemLink(l.itemId, l.name), sort: (l) => l.name ?? name(l.itemId) },
      { label: 'Amount', get: (l) => num(l.amount), sort: (l) => l.amount, cls: 'r' },
      { label: 'Your price', get: (l) => money(l.price), sort: (l) => l.price, cls: 'r' },
      ...(venue === 'bazaar' ? [{ label: 'Market value', get: (l: MyListing) => money(l.value ?? items.get(l.itemId)?.marketValue ?? null), sort: (l: MyListing) => l.value ?? 0, cls: 'r' }] : []),
      {
        label: 'Cheapest on the Item Market',
        get: (l) => {
          const kept = cheapestKept.get(l.itemId);
          const b = h('button', { type: 'button', class: 'btn-small', 'data-action': 'load-price' }, 'Load') as HTMLButtonElement;
          b.addEventListener('click', () => void load(l.itemId));
          const c = h('span', { 'data-cheapest': String(l.itemId) }, b);
          cells.set(l.key, c);
          if (kept && Date.now() - kept.at < MARKET_KEEP_MS) queueMicrotask(() => fill(l, kept.price));
          return c;
        },
        cls: 'r',
      },
      {
        label: 'You vs cheapest',
        get: (l) => {
          const c = h('span', { 'data-vs-cell': l.key }, h('span', { class: 'muted' }, '—'));
          vsCells.set(l.key, c);
          return c;
        },
      },
    ], { initial: 0, desc: false, empty: venue === 'market' ? 'Nothing listed on the Item Market.' : 'Your bazaar is empty.' });
    return h('div', {}, h('div', { class: 'toolbar' }, all, note), t, h('p', { class: 'muted small' }, 'Green: nobody lists it for less (your own listing may be the cheapest). Red: someone does. Prices come from the Item Market through the Omni-Torn server (kept 3 minutes; your key isn’t used) — when its share for you is used, your own key reads them.'));
  }

  async function listings(venue: Venue): Promise<MyListing[]> {
    if (venue === 'bazaar') return readMyBazaar(await torn('/user/', { selections: 'bazaar' }, 60_000)) ?? [];
    const out: MyListing[] = [];
    for (let page = 0; page < LISTING_PAGES; page++) {
      const got = readMyItemMarket(await torn('/v2/user/itemmarket', { offset: String(page * 100) }, 60_000)) ?? [];
      out.push(...got);
      if (got.length < 100) break;
    }
    return out;
  }

  function drawListings(venue: Venue): HTMLElement {
    const out = box(venue === 'market' ? 'Your listings now' : 'Your bazaar now', loading(venue === 'market' ? 'Reading your Item Market listings…' : 'Reading your bazaar…'), { id: `${venue}-listings` });
    const panel = out.querySelector('.panel-body')!;
    void (async () => {
      try {
        const list = await listings(venue);
        const w = listingsWorth(list);
        panel.replaceChildren(
          h('div', { class: 'tiles' }, tile(venue === 'market' ? 'Listings' : 'Items', num(list.length), `${venue}-count`), tile('Units', num(w.units), `${venue}-units`), tile('Worth at your prices', money(w.worth), `${venue}-worth`), ...(w.value !== null ? [tile('At market value', money(w.value), `${venue}-value`)] : [])),
          listingTable(list, venue),
        );
      } catch (err) {
        panel.replaceChildren(problem(err));
      }
    })();
    return out;
  }

  // ------------------------------------------------------------ the log (the kit)

  let store: LogStore | null = null;
  let reader: LogReaderLike | null = null;
  let first: Promise<void> | null = null;
  const logNote = h('span', { class: 'muted small', 'data-log-note': '' });

  async function ensureReader(): Promise<LogReaderLike> {
    if (reader) return reader;
    store = await logStore(pid);
    // Torn's "Item market" and "Bazaar" log categories, by name; without both, the whole log (trades picked out here).
    let cats: [number | null, number | null] = [null, null];
    try {
      const list = await torn('/v2/torn/logcategories', {}, DAY);
      cats = [findLogCategory(list, /^item\s*market$/i), findLogCategory(list, /^bazaars?$/i)];
    } catch {
      /* the whole log */
    }
    const streams: LogStream[] = cats[0] && cats[1] ? cats.map((c) => ({ id: `cat:${c}`, cat: c })) : [{ id: 'all' }];
    reader = streams.length === 1 ? new LogReader(store, streams[0]!) : new MultiReader(streams.map((s) => new LogReader(store!, s)));
    return reader;
  }

  function drawLog(): HTMLElement {
    const out = h('div', {}, loading(readingLogText()));
    void (async () => {
      try {
        const r = await ensureReader();
        first ??= firstRead(r).then(
          (t) => void (logNote.textContent = t),
          (err: unknown) => void logNote.replaceChildren(problem(err)),
        );
        await first;
        if (out.isConnected) await fillLog(out);
      } catch (err) {
        out.replaceChildren(problem(err));
      }
    })();
    return out;
  }

  async function fillLog(out: HTMLElement): Promise<void> {
    const r = await ensureReader();
    const venue: Venue | null = tab === 'market' || tab === 'bazaar' ? tab : null;
    const all = (await store!.range({ from: null, to: null })).flatMap((l) => readTradeLine(l));
    const mine = filterTrades(all, { venue });
    const inDates = mine.filter((l) => inRange(l.at, range));
    const shown = filterTrades(inDates, { side, itemId: itemFilter });
    const reread = () => void fillLog(out);
    const onRange = (nr: DateRange) => {
      range = nr;
      setQ();
      reread();
    };
    const kept = await keptLogPanel({
      reader: r,
      store: store!,
      range,
      matching: mine.length,
      what: venue ? `${VENUE_NAME[venue]} buys and sales` : 'Item Market and bazaar buys and sales',
      note: logNote,
      forgetText: forgetLogText(),
      onForget: () => {
        reader = null;
        store = null;
        first = null;
      },
      redraw: reread,
    });
    const picker = rangePicker(range, onRange);
    // Filters: buys or sales, one item (those traded in these dates).
    const traded = [...new Set(inDates.map((l) => l.itemId))].sort((a, b) => name(a).localeCompare(name(b)));
    const filters = h(
      'div',
      { class: 'toolbar' },
      filterSelect('Buys or sales', [['', 'Buys and sales'], ['buy', 'Buys only'], ['sell', 'Sales only']], side ?? '', (v) => {
        side = v === 'buy' || v === 'sell' ? v : null;
        setQ();
        reread();
      }, 'side'),
      filterSelect('Item', [['', 'Every item'], ...traded.map((id) => [String(id), name(id)] as const)], itemFilter ? String(itemFilter) : '', (v) => {
        itemFilter = Number(v) || null;
        setQ();
        reread();
      }, 'item'),
    );
    const t = tradeTotals(shown);
    const tiles = h(
      'div',
      { class: 'tiles' },
      tile('Bought', `${num(t.bought)} for ${money(t.spent)}`, 'bought'),
      tile('Sold', `${num(t.sold)} for ${money(t.gross)}`, 'sold'),
      ...(venue !== 'bazaar' ? [tile('Item Market fees', money(t.fees), 'fees')] : []),
      tile('Cash in − out', moneySigned(t.cash), 'cash', tone(t.cash)),
      tile('Profit on what you sold', moneySigned(t.profit), 'profit', tone(t.profit)),
    );
    const now = Date.now();
    const chart = box(h('h2', {}, 'Cash in − out per day'), zoomChart(sumPerDay(shown, (l) => cashOf(l) ?? 0, range, now), { label: 'Cash in − out', format: (x) => `${x < 0 ? '−' : ''}$${short(Math.abs(x))}`, onZoom: onRange }), { id: 'trade-chart' });
    const rows = ledgerByItem(shown);
    const byItem = box('By item', [
      table(rows, [
        { label: 'Item', get: (x) => itemLink(x.itemId), sort: (x) => name(x.itemId) },
        { label: 'Bought', get: (x) => (x.bought ? num(x.bought) : '—'), sort: (x) => x.bought, cls: 'r' },
        { label: 'Avg buy', get: (x) => money(x.avgBuy), sort: (x) => x.avgBuy ?? 0, cls: 'r' },
        { label: 'Sold', get: (x) => (x.sold ? num(x.sold) : '—'), sort: (x) => x.sold, cls: 'r' },
        { label: 'Avg sale (after fees)', get: (x) => money(x.avgSell), sort: (x) => x.avgSell ?? 0, cls: 'r' },
        ...(venue !== 'bazaar' ? [{ label: 'Fees', get: (x: (typeof rows)[number]) => (x.fees ? money(x.fees) : '—'), sort: (x: (typeof rows)[number]) => x.fees, cls: 'r' }] : []),
        { label: 'Profit', get: (x) => (x.profit === null ? h('span', { class: 'muted', title: x.sold ? 'Nothing bought in these dates to cost it' : '' }, '—') : h('span', { class: tone(x.profit) }, moneySigned(x.profit))), sort: (x) => x.profit ?? -Infinity, cls: 'r' },
      ], { initial: 6, empty: 'No buys or sales in this range.' }),
      h('p', { class: 'muted small' }, `Profit counts each item you sold at the average price you paid for it in these dates (after the Item Market’s fee)${t.uncosted ? ` — ${num(t.uncosted)} sold without a buy in these dates aren’t counted` : ''}. “Cash in − out” is simply what came in less what you spent. The Profit tab matches each sale to your earliest buys instead (first in, first out), trades included.`),
    ], { id: 'trade-items' });
    const venues = venue === null
      ? box('By place', table((['market', 'bazaar'] as const).map((v) => ({ v, t: tradeTotals(filterTrades(shown, { venue: v })) })), [
          { label: 'Where', get: (x) => VENUE_NAME[x.v] },
          { label: 'Bought', get: (x) => `${num(x.t.bought)} · ${money(x.t.spent)}`, cls: 'r' },
          { label: 'Sold', get: (x) => `${num(x.t.sold)} · ${money(x.t.gross)}`, cls: 'r' },
          { label: 'Fees', get: (x) => money(x.t.fees), cls: 'r' },
          { label: 'Cash in − out', get: (x) => h('span', { class: tone(x.t.cash) }, moneySigned(x.t.cash)), cls: 'r' },
          { label: 'Profit', get: (x) => h('span', { class: tone(x.t.profit) }, moneySigned(x.t.profit)), cls: 'r' },
        ]), { id: 'trade-venues' })
      : null;
    const csv = csvButton(() => `${venue ?? 'trading'}-${rangeText(range).replace(/[^\w]+/g, '-').toLowerCase()}.csv`, () => tradesCsvRows(shown, name));
    const lines = box('Buys and sales', [
      table(shown.slice(0, 500), [
        { label: 'When', get: (l) => h('span', { title: new Date(l.at).toUTCString() }, tct(l.at)), sort: (l) => l.at },
        ...(venue === null ? [{ label: 'Where', get: (l: TradeLine) => VENUE_NAME[l.venue] }] : []),
        { label: 'What', get: (l) => h('span', { class: l.side === 'buy' ? 't-red' : 't-green' }, l.side === 'buy' ? 'Bought' : 'Sold'), sort: (l) => l.side },
        { label: 'Item', get: (l) => itemLink(l.itemId), sort: (l) => name(l.itemId) },
        { label: 'Amount', get: (l) => num(l.qty), sort: (l) => l.qty, cls: 'r' },
        { label: 'Each', get: (l) => money(l.each), sort: (l) => l.each ?? 0, cls: 'r' },
        { label: 'Cash', get: (l) => h('span', { class: tone(cashOf(l)) }, moneySigned(cashOf(l))), sort: (l) => cashOf(l) ?? 0, cls: 'r' },
      ], { csv: false, initial: 0, empty: 'Nothing in this range.' }),
      shown.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(shown.length)} — the CSV has them all.`) : null,
    ].filter(Boolean) as HTMLElement[], { id: 'trade-lines', extra: csv });
    out.replaceChildren(...([kept, picker, filters, tiles, chart, venues, byItem, lines].filter(Boolean) as HTMLElement[]));
  }

  // ------------------------------------------------------------ profit: first in, first out (S27)

  let receipts: TradeReceipt[] | null = null;
  let receiptsProblem = '';
  const byName = new Map<string, number>();
  const idOf = (n: string) => {
    if (!byName.size) for (const it of items.values()) byName.set(it.name.toLowerCase(), it.id);
    return byName.get(n.trim().toLowerCase()) ?? null;
  };

  async function loadReceipts(): Promise<TradeReceipt[]> {
    if (receipts) return receipts;
    try {
      const doc = await server<{ lists: Record<string, unknown> | null }>('GET', '/api/me/lists');
      receipts = receiptsOf(doc.lists);
      receiptsProblem = '';
    } catch (err) {
      receipts = [];
      receiptsProblem = err instanceof Error ? err.message : 'Couldn’t read your receipts.';
    }
    return receipts;
  }

  /** Take a receipt out of your lists (a trade that never went through): the server's copy fresh, sent back once more if another device wrote between. */
  async function removeReceipt(id: string): Promise<void> {
    for (let i = 0; i < 2; i++) {
      const doc = await server<{ lists: Record<string, unknown> | null; updatedAt: number | null }>('GET', '/api/me/lists');
      const r = await server<{ applied: boolean; lists: Record<string, unknown> }>('PUT', '/api/me/lists', { lists: withoutReceipt(doc.lists, id), updatedAt: Date.now(), device: 'Website', base: doc.updatedAt ?? null });
      if (r.applied) {
        receipts = receiptsOf(r.lists);
        return;
      }
    }
    throw new Error('Another device changed your lists just now — try again.');
  }

  function drawProfit(): HTMLElement {
    const out = h('div', {}, loading(`${readingLogText().replace(/…$/, '')} and your receipts…`));
    const withLog = keyMeets(session.get()?.access ?? null, LOG_NEED);
    void (async () => {
      try {
        if (withLog) {
          const r = await ensureReader();
          first ??= firstRead(r).then(
            (t) => void (logNote.textContent = t),
            (err: unknown) => void logNote.replaceChildren(problem(err)),
          );
        }
        await Promise.all([withLog ? first : null, loadReceipts()]);
        if (out.isConnected) await fillProfit(out, withLog);
      } catch (err) {
        out.replaceChildren(problem(err));
      }
    })();
    return out;
  }

  const partnerOf = (r: TradeReceipt) =>
    r.partner?.id ? h('a', { href: `https://www.torn.com/profiles.php?XID=${r.partner.id}`, target: '_blank', rel: 'noopener' }, r.partner.name ?? `[${r.partner.id}]`) : r.partner?.name ?? h('span', { class: 'muted' }, '—');
  const sideText = (s: TradeReceipt['gave']) => [s.money ? money(s.money) : '', ...s.items.map((i) => `${num(i.qty)}× ${i.name}`)].filter(Boolean).join(', ') || '—';

  async function fillProfit(out: HTMLElement, withLog: boolean): Promise<void> {
    const reread = () => void fillProfit(out, withLog);
    const onRange = (nr: DateRange) => {
      range = nr;
      setQ();
      reread();
    };
    // Everything you have — the whole kept log and every receipt — matched first in, first out; then the sales in view.
    const logLines: ProfitLine[] = withLog && store ? (await store.range({ from: null, to: null })).flatMap((l) => readTradeLine(l)).map(fromTradeLine) : [];
    const rc = receipts ?? [];
    const rcLines = rc.flatMap((r) => receiptLines(r, idOf));
    const { sales, open } = fifoMatch([...logLines, ...rcLines]);
    const inDates = sales.filter((s) => inRange(s.line.at, range) && (!source || s.line.source === source));
    const shown = inDates.filter((s) => !itemFilter || s.line.itemId === itemFilter);
    const t = profitTotals(shown);

    const logPart = withLog && reader && store
      ? await keptLogPanel({
          reader,
          store,
          range,
          matching: logLines.length,
          what: 'Item Market and bazaar buys and sales',
          note: logNote,
          forgetText: forgetLogText(),
          onForget: () => {
            reader = null;
            store = null;
            first = null;
          },
          redraw: reread,
        })
      : lockedCard('From your log', LOG_NEED, 'your Item Market and bazaar buys and sales count too — without it, only your trades’ receipts are matched.');
    const traded = [...new Set(inDates.map((s) => s.line.itemId))].sort((a, b) => name(a).localeCompare(name(b)));
    const filters = h(
      'div',
      { class: 'toolbar' },
      filterSelect('Where', [['', 'Everywhere'], ...SOURCES.map((x) => [x, SOURCE_NAME[x]] as const)], source ?? '', (v) => {
        source = SOURCES.find((x) => x === v) ?? null;
        setQ();
        reread();
      }, 'source'),
      filterSelect('Item', [['', 'Every item'], ...traded.map((id) => [String(id), name(id)] as const)], itemFilter ? String(itemFilter) : '', (v) => {
        itemFilter = Number(v) || null;
        setQ();
        reread();
      }, 'item'),
    );
    const tiles = h(
      'div',
      { class: 'tiles' },
      tile('Profit (first in, first out)', moneySigned(t.profit), 'fifo-profit', tone(t.profit)),
      tile('Sold', `${num(t.sold)} in ${num(t.sales)} sale${t.sales === 1 ? '' : 's'}`, 'fifo-sold'),
      tile('Cost of what sold', money(t.cost), 'fifo-cost'),
      tile('Margin', t.margin === null ? '—' : `${t.margin > 0 ? '+' : ''}${num(t.margin, 1)}%`, 'fifo-margin', tone(t.margin)),
      tile('Not counted', t.uncosted ? `${num(t.uncosted)} unit${t.uncosted === 1 ? '' : 's'}` : 'none', 'fifo-uncosted'),
    );
    const now = Date.now();
    const chart = box(h('h2', {}, 'Profit per day'), zoomChart(sumPerDay(shown.map((s) => ({ at: s.line.at, v: s.profit ?? 0 })), (x) => x.v, range, now), { label: 'Profit', format: (x) => `${x < 0 ? '−' : ''}$${short(Math.abs(x))}`, onZoom: onRange }), { id: 'fifo-chart' });
    const openInView = open.filter((o) => !itemFilter || o.itemId === itemFilter);
    const rows = profitByItem(shown, openInView);
    const byItem = box('Profit by item', [
      table(rows, [
        { label: 'Item', get: (x) => itemLink(x.itemId), sort: (x) => name(x.itemId) },
        { label: 'Sold', get: (x) => num(x.sold), sort: (x) => x.sold, cls: 'r' },
        { label: 'Brought in', get: (x) => (x.costed ? money(x.proceeds) : '—'), sort: (x) => x.proceeds, cls: 'r' },
        { label: 'Cost', get: (x) => (x.costed ? money(x.cost) : '—'), sort: (x) => x.cost, cls: 'r' },
        { label: 'Profit', get: (x) => (x.profit === null ? h('span', { class: 'muted' }, '—') : h('span', { class: tone(x.profit) }, moneySigned(x.profit))), sort: (x) => x.profit ?? -Infinity, cls: 'r' },
        { label: 'Margin', get: (x) => (x.margin === null ? '—' : `${num(x.margin, 1)}%`), sort: (x) => x.margin ?? -Infinity, cls: 'r' },
        { label: 'Not counted', get: (x) => (x.uncosted ? num(x.uncosted) : '—'), sort: (x) => x.uncosted, cls: 'r' },
        { label: 'Still held', get: (x) => (x.held ? `${num(x.held)} · ${money(x.heldCost)}${x.heldUnknown ? ` (+${num(x.heldUnknown)} unpriced)` : ''}` : '—'), sort: (x) => x.held, cls: 'r' },
      ], { initial: 4, empty: 'No sales in this range.' }),
      h('p', { class: 'muted small' }, 'Each sale is matched to the earliest buys of that item you still held (first in, first out), across the Item Market, bazaars and trades, over everything kept — so a sale finds buys made before these dates. “Not counted”: units sold from a buy whose price isn’t known, sold without a price, or with nothing bought before them (found, gifted, or bought before the kept log starts) — their profit is left out, never guessed. “Still held”: bought and not sold since, at cost (items you used or gave away still show here, and later sales take them first).'),
    ], { id: 'fifo-items' });
    const lotsText = (s: MatchedSale) => {
      if (!s.lots.length) return h('span', { class: 'muted' }, 'no buy before it');
      const where = [...new Set(s.lots.map((l) => SOURCE_NAME[l.source]))].join(' + ');
      const unpriced = s.lots.filter((l) => l.each === null).reduce((n, l) => n + l.qty, 0);
      return `${num(s.lots.length)} buy${s.lots.length === 1 ? '' : 's'} (${where}, from ${tct(s.lots[0]!.at)})${s.noBuy ? ` · ${num(s.noBuy)} without a buy` : ''}${unpriced ? ` · ${num(unpriced)} unpriced` : ''}`;
    };
    const newest = [...shown].reverse();
    const csv = csvButton(() => `profit-${rangeText(range).replace(/[^\w]+/g, '-').toLowerCase()}.csv`, () => profitCsvRows(newest, name));
    const salesBox = box('Each sale', [
      table(newest.slice(0, 500), [
        { label: 'When', get: (s) => h('span', { title: new Date(s.line.at).toUTCString() }, tct(s.line.at)), sort: (s) => s.line.at },
        { label: 'Where', get: (s) => SOURCE_NAME[s.line.source], sort: (s) => s.line.source },
        { label: 'Item', get: (s) => itemLink(s.line.itemId), sort: (s) => name(s.line.itemId) },
        { label: 'Amount', get: (s) => num(s.line.qty), sort: (s) => s.line.qty, cls: 'r' },
        { label: 'Sold at', get: (s) => money(saleEach(s)), sort: (s) => saleEach(s) ?? 0, cls: 'r' },
        { label: 'Cost each', get: (s) => money(costEach(s)), sort: (s) => costEach(s) ?? 0, cls: 'r' },
        { label: 'Profit', get: (s) => (s.profit === null ? h('span', { class: 'muted' }, '—') : h('span', { class: tone(s.profit) }, moneySigned(s.profit))), sort: (s) => s.profit ?? -Infinity, cls: 'r' },
        { label: 'Bought from', get: (s) => lotsText(s) },
      ], { csv: false, initial: 0, empty: 'No sales in this range.' }),
      shown.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(shown.length)} — the CSV has them all.`) : null,
    ].filter(Boolean) as HTMLElement[], { id: 'fifo-sales', extra: csv });

    // Your trades' receipts (the extension's trade helper, with your lists).
    const rcNote = h('span', { class: 'muted small', 'data-receipts-note': '' }, receiptsProblem);
    const rcTable = table(rc, [
      { label: 'When', get: (r) => h('span', { title: new Date(r.at).toUTCString() }, tct(r.at)), sort: (r) => r.at },
      { label: 'With', get: partnerOf, sort: (r) => r.partner?.name ?? '' },
      { label: 'You gave', get: (r) => sideText(r.gave) },
      { label: 'You got', get: (r) => sideText(r.got) },
      { label: 'Receipt', get: (r) => `${num(r.pct)}%`, sort: (r) => r.pct, cls: 'r' },
      {
        label: '',
        get: (r) => {
          const b = h('button', { type: 'button', class: 'btn-small', 'data-action': 'remove-receipt', title: 'A trade that never went through: take its receipt out (on every device)' }, 'Remove') as HTMLButtonElement;
          b.addEventListener('click', async () => {
            b.disabled = true;
            try {
              await removeReceipt(r.id);
              reread();
            } catch (err) {
              rcNote.textContent = err instanceof Error ? err.message : 'Couldn’t remove it.';
              b.disabled = false;
            }
          });
          return b;
        },
      },
    ], { initial: 0, empty: 'No receipts yet.' });
    const rcBox = box(`Your trades’ receipts (${num(rc.length)})`, [
      rcNote,
      rcTable,
      h('p', { class: 'muted small' }, 'Kept when you press “Copy receipt” in the Omni-Torn trade helper on a trade (the extension or Torn PDA; one per trade, the last copy counts) and carried here with your lists. What you got counts as bought and what you gave as sold; the money that went the other way pays for them, split by the receipt’s prices (its % of them for what you got). In a swap each item is valued at the receipt’s prices. Items whose price the helper didn’t know are left out unless the money settles them.'),
    ], { id: 'fifo-receipts' });

    out.replaceChildren(logPart, rangePicker(range, onRange), filters, tiles, chart, byItem, salesBox, rcBox);
  }

  // ------------------------------------------------------------ the page

  function draw(): void {
    const parts: HTMLElement[] = [];
    if (tab === 'profit') {
      body.replaceChildren(drawProfit());
      return;
    }
    const what = tab === 'totals' ? 'your log shows every Item Market and bazaar buy and sale.' : `your log shows every ${VENUE_NAME[tab]} buy and sale.`;
    if (tab === 'market') parts.push(gated('Your listings now', LISTINGS_NEED, 'Torn lists your Item Market listings for a Limited key.', () => drawListings('market')));
    if (tab === 'bazaar') parts.push(drawListings('bazaar'));
    parts.push(gated('From your log', LOG_NEED, what, drawLog));
    body.replaceChildren(...parts);
  }

  // Torn's items (names, market values — the server's list, none of your calls) before drawing names.
  status.textContent = 'Reading Torn’s items…';
  try {
    items = new Map((await ref('items')).data.map((i) => [i.id, i]));
    status.textContent = '';
  } catch (err) {
    status.replaceChildren(problem(err));
  }
  if (!view.isConnected) return;
  draw();
}
